
import React, { useMemo, useState } from 'react';
import { Editor } from '@tiptap/react';
import { Image, Link, Video, Loader2, Upload } from 'lucide-react';
import EditorButton from '../buttons/EditorButton';
import EditorButtonGroup from '../buttons/EditorButtonGroup';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import LinkDialog from './dialogs/LinkDialog';
import VideoDialog from './dialogs/VideoDialog';
import { useLinkDialog } from './hooks/useLinkDialog';
import { useVideoDialog } from './hooks/useVideoDialog';

interface MediaButtonsProps {
  editor: Editor;
  shopId?: string;
  organizationId?: string;
}

const MediaButtons = ({ editor }: MediaButtonsProps) => {
  // State for media dialog
  const [isMediaDialogOpen, setIsMediaDialogOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Link dialog hook
  const linkDialogData = useLinkDialog(editor);
  
  // Video dialog hook
  const videoDialogData = useVideoDialog(editor);

  // Handle file selection
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);

    // Create data URL for the image
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setPreviewUrl(dataUrl);
      setIsUploading(false);
    };
    reader.readAsDataURL(file);
  };

  // Handle image confirmation
  const handleConfirmImage = () => {
    if (previewUrl && editor) {
      editor.chain().focus().setImage({ src: previewUrl }).run();
      setPreviewUrl(null);
      setIsMediaDialogOpen(false);
    }
  };

  // Handle cancellation
  const handleCancelImage = () => {
    setPreviewUrl(null);
  };

  return (
    <>
      <EditorButtonGroup>
        <EditorButton
          onClick={() => setIsMediaDialogOpen(true)}
          title="Insérer une image"
          icon={<Image className="h-4 w-4" />}
          disabled={!editor || !editor.can().chain().focus()}
        />
        
        <EditorButton
          onClick={linkDialogData.openLinkDialog}
          title="Insérer un lien"
          icon={<Link className="h-4 w-4" />}
          isActive={editor.isActive('link')}
          disabled={!editor || !editor.can().chain().focus()}
        />
        
        <EditorButton
          onClick={videoDialogData.openVideoDialog}
          title="Insérer une vidéo YouTube"
          icon={<Video className="h-4 w-4" />}
          disabled={!editor || !editor.can().chain().focus()}
        />
      </EditorButtonGroup>

      <Dialog open={isMediaDialogOpen} onOpenChange={setIsMediaDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Ajouter une image</DialogTitle>
          </DialogHeader>

          {previewUrl ? (
            <div className="space-y-4">
              <div className="relative aspect-video w-full overflow-hidden rounded-lg">
                <img
                  src={previewUrl}
                  alt="Prévisualisation"
                  className="object-cover w-full h-full"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={handleCancelImage}
                  disabled={isUploading}
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleConfirmImage}
                  disabled={isUploading}
                >
                  {isUploading ? 'Upload en cours...' : 'Confirmer'}
                </Button>
              </div>
            </div>
          ) : (
            <div 
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => {
                const fileInput = document.getElementById('editor-file-upload') as HTMLInputElement;
                if (fileInput) fileInput.click();
              }}
            >
              {isUploading ? (
                <div className="flex flex-col items-center">
                  <Loader2 className="h-8 w-8 text-gray-400 mx-auto mb-2 animate-spin" />
                  <p className="text-sm text-gray-500">Téléchargement en cours...</p>
                </div>
              ) : (
                <>
                  <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Cliquez pour parcourir vos fichiers</p>
                </>
              )}
              <input 
                id="editor-file-upload"
                type="file" 
                onChange={handleFileChange} 
                className="hidden" 
                accept="image/*"
                disabled={isUploading}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <LinkDialog
        isOpen={linkDialogData.isLinkDialogOpen}
        onOpenChange={linkDialogData.setIsLinkDialogOpen}
        linkUrl={linkDialogData.linkUrl}
        setLinkUrl={linkDialogData.setLinkUrl}
        addLink={linkDialogData.addLink}
        editor={editor}
      />

      <VideoDialog
        isOpen={videoDialogData.isVideoDialogOpen}
        onOpenChange={videoDialogData.setIsVideoDialogOpen}
        videoUrl={videoDialogData.videoUrl}
        setVideoUrl={videoDialogData.setVideoUrl}
        addVideo={videoDialogData.addVideo}
        editor={editor}
      />
    </>
  );
};

export default React.memo(MediaButtons);
